// ═══════════════════════════════════════════════════════════════
// export-data — get a mechanic's own data out of the app.
//
// Three jobs at once:
//   1. "Can I get my data out?" — a question every buyer of a paid
//      product eventually asks, and the honest answer has to be yes.
//   2. The accountant's spreadsheet, which has been on the roadmap
//      since Phase 3.
//   3. The safety net under destructive deletes: right now removing a
//      customer wipes years of history with no undo.
//
// Everything here is pure and synchronous so the CSV rules can be
// tested directly — that's where the bugs in exports live.
// ═══════════════════════════════════════════════════════════════
import {
  toDate, formatPhone, computeLaborSubtotal, computePartsSubtotal,
  computeJobTax, computeGrandTotal,
} from './job-helpers.js';
import { normalizeRecommendations, splitByStatus } from './recommendations.js';

// ─── CSV ────────────────────────────────────────────────────────
// A spreadsheet cell beginning with = + - or @ is interpreted as a
// FORMULA by Excel and Sheets. A job described as "-4 qt oil" becomes
// a broken cell, and a hostile value becomes something worse. Prefix
// with an apostrophe so the text is shown as typed.
//
// This matters here specifically because the whole point of the CSV
// is that a mechanic opens it in Excel.
const FORMULA_LEADERS = ['=', '+', '-', '@', '\t', '\r'];

export function csvCell(value) {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (s.length > 0 && FORMULA_LEADERS.includes(s[0])) {
    s = `'${s}`;
  }
  // Quote when the value contains anything that would break the row,
  // doubling any quotes of its own.
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function toCsv(columns, rows) {
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((row) =>
    columns.map((c) => csvCell(c.get(row))).join(',')
  );
  // CRLF: Excel is happiest with it, and every other tool copes.
  return [header, ...body].join('\r\n') + '\r\n';
}

// ISO dates rather than locale dates — unambiguous across countries
// and they sort correctly as text in a spreadsheet.
export function isoDate(v) {
  const d = toDate(v);
  if (!d || isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
export function isoDateTime(v) {
  const d = toDate(v);
  if (!d || isNaN(d.getTime())) return '';
  return d.toISOString();
}

function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(2) : '';
}

// ─── Datasets ───────────────────────────────────────────────────
export function customersCsv(customers) {
  return toCsv(
    [
      { label: 'Name',              get: (c) => c.name },
      { label: 'Phone',             get: (c) => formatPhone(c.phone) },
      { label: 'Email',             get: (c) => c.email },
      { label: 'Address',           get: (c) => c.address },
      { label: 'Preferred contact', get: (c) => (c.preferredContact || []).join(' / ') },
      { label: 'Notes',             get: (c) => c.notes },
      { label: 'Added',             get: (c) => isoDate(c.createdAt) },
    ],
    customers
  );
}

export function vehiclesCsv(vehicles, customerById = {}) {
  return toCsv(
    [
      { label: 'Customer', get: (v) => customerById[v.customerId]?.name || '' },
      { label: 'Year',     get: (v) => v.year },
      { label: 'Make',     get: (v) => v.make },
      { label: 'Model',    get: (v) => v.model },
      { label: 'Trim',     get: (v) => v.trim },
      { label: 'VIN',      get: (v) => v.vin },
      { label: 'Plate',    get: (v) => v.plate },
      { label: 'Color',    get: (v) => v.color },
      { label: 'Odometer', get: (v) => v.odometer },
    ],
    vehicles
  );
}

// The accountant's sheet. One row per job, money broken out the way
// a bookkeeper needs it rather than the way the app stores it.
export function jobsCsv(jobs, { customerById = {}, vehicleById = {} } = {}) {
  const vehicleText = (j) => {
    const v = vehicleById[j.vehicleId];
    if (!v) return '';
    return [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');
  };
  return toCsv(
    [
      { label: 'Number',        get: (j) => (j.jobNumber ?? '') },
      { label: 'Status',        get: (j) => j.status },
      { label: 'Customer',      get: (j) => customerById[j.customerId]?.name || '' },
      { label: 'Vehicle',       get: (j) => vehicleText(j) },
      { label: 'Description',   get: (j) => j.description },
      { label: 'Scheduled',     get: (j) => isoDate(j.scheduledAt) },
      { label: 'Sent',          get: (j) => isoDate(j.sentAt) },
      { label: 'Approved',      get: (j) => isoDate(j.approvedAt) },
      { label: 'Invoiced',      get: (j) => isoDate(j.invoicedAt) },
      { label: 'Paid',          get: (j) => isoDate(j.paidAt) },
      { label: 'Closed',        get: (j) => isoDate(j.closedAt) },
      { label: 'Cancelled',     get: (j) => isoDate(j.cancelledAt) },
      { label: 'Cancel reason', get: (j) => j.cancelReason || '' },
      { label: 'Labor',         get: (j) => money(computeLaborSubtotal(j.lineItems)) },
      { label: 'Parts',         get: (j) => money(computePartsSubtotal(j.lineItems)) },
      { label: 'Shop supplies', get: (j) => money(j.shopSupplies) },
      // Subtotal, then tax, then what the customer paid — in that
      // order, because that's how the figures are used at tax time.
      // 'Subtotal' is the same number the column used to call 'Total'
      // and still reads j.totalPrice, so a year-on-year comparison of
      // earnings lines up. Tax is broken out because it is owed to
      // somebody else and has to be filed separately from income.
      { label: 'Subtotal',      get: (j) => money(j.totalPrice) },
      { label: 'Tax rate parts', get: (j) => (j.tax?.partsRate ?? '') },
      { label: 'Tax rate labor', get: (j) => (j.tax?.laborRate ?? '') },
      { label: 'Tax area',      get: (j) => j.tax?.label || '' },
      { label: 'Sales tax',     get: (j) => money(computeJobTax(j).total) },
      { label: 'Total charged', get: (j) => money(computeGrandTotal(j)) },
      { label: 'Paid how',      get: (j) => j.closedBy || '' },
      { label: 'Minutes on site', get: (j) => (j.workMinutes ?? '') },
      { label: 'Odometer in',   get: (j) => (j.odometerIn ?? '') },
      { label: 'Odometer out',  get: (j) => (j.odometerOut ?? '') },
      { label: 'Work done',     get: (j) => j.workSummary || '' },
    ],
    jobs
  );
}

// What was offered and what they said — the record behind every
// "you passed on this last time" conversation.
export function recommendationsCsv(jobs, { customerById = {} } = {}) {
  const rows = [];
  jobs.forEach((job) => {
    normalizeRecommendations(job.recommendations).forEach((r) => {
      rows.push({ job, rec: r });
    });
  });
  return toCsv(
    [
      { label: 'Job number',  get: (x) => (x.job.jobNumber ?? '') },
      { label: 'Customer',    get: (x) => customerById[x.job.customerId]?.name || '' },
      { label: 'Date',        get: (x) => isoDate(x.job.decidedAt || x.job.createdAt) },
      { label: 'Recommended', get: (x) => x.rec.label },
      { label: 'Detail',      get: (x) => x.rec.detail },
      { label: 'Urgency',     get: (x) => (x.rec.urgency === 'replace' ? 'Urgent' : 'Soon') },
      { label: 'Price',       get: (x) => money(x.rec.price) },
      { label: 'Answer',      get: (x) => x.rec.status },
    ],
    rows
  );
}

// ─── Full bundle ────────────────────────────────────────────────
// Everything, in the shape it's stored, so it can be read back or
// moved somewhere else. Photos are URLs, not files — an honest
// limitation, called out in the manifest rather than left to be
// discovered.
export function buildJsonBundle({
  customers = [], vehicles = [], jobs = [], inspections = [], profile = null,
  appVersion = '', userEmail = '',
}) {
  return {
    _about: {
      product: 'Bayless',
      exportedAt: new Date().toISOString(),
      exportedBy: userEmail,
      appVersion,
      note:
        'Photos are recorded as URLs, not embedded files. Those links keep working while ' +
        'the account exists. To keep the images themselves, download them separately.',
      counts: {
        customers: customers.length,
        vehicles: vehicles.length,
        jobs: jobs.length,
        inspections: inspections.length,
      },
    },
    profile,
    customers,
    vehicles,
    jobs,
    inspections,
  };
}

// Summary shown before anyone clicks — so the export is a known
// quantity rather than a surprise.
export function exportSummary({ customers = [], vehicles = [], jobs = [], inspections = [] }) {
  const recs = jobs.reduce(
    (n, j) => n + normalizeRecommendations(j.recommendations).length, 0
  );
  const declined = jobs.reduce(
    (n, j) => n + splitByStatus(j.recommendations).declined.length, 0
  );
  return {
    customers: customers.length,
    vehicles: vehicles.length,
    jobs: jobs.length,
    inspections: inspections.length,
    recommendations: recs,
    declined,
  };
}

// ─── Download ───────────────────────────────────────────────────
export function downloadText(filename, text, mime = 'text/csv;charset=utf-8') {
  // The BOM makes Excel read UTF-8 correctly — without it, an accented
  // customer name arrives mangled.
  const needsBom = mime.startsWith('text/csv');
  const blob = new Blob([needsBom ? '﻿' + text : text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function stamp() {
  return new Date().toISOString().slice(0, 10);
}
